import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, makeStyles, TextField, withStyles } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import PropTypes from "prop-types";
import { useTranslate } from "react-polyglot";
import Typography from "@material-ui/core/Typography";
import PhotoCameraRoundedIcon from "@material-ui/icons/PhotoCameraRounded";
import WallpaperRoundedIcon from "@material-ui/icons/WallpaperRounded";
import { dataURLToBlob, resizeImage } from "../../../../../Utils/misc";
import FlexboxVertical from "../../../../../SharedComponents/FlexboxVertical.jsx";
import { useAnswerUserFiles } from "../../../../../Providers/Data/AnswerUserFilesProvider.jsx";
import { useBaseData } from "../../../../../Providers/Data/BaseDataProvider.jsx";
import FlexboxHorizontal from "../../../../../SharedComponents/FlexboxHorizontal.jsx";
import WebCamDialog from "./WebCamDialog.jsx";
import VideoCamButton from "./VideoCamButton.jsx";
import MyBackdrop from "../../../../../SharedComponents/MyBackdrop.jsx";
import ImageViewerDialog from "./ImageViewerDialog.jsx";

const StyledButton = withStyles((theme) => ({
  root: {
    color: theme.palette.text.disabled,
    "& svg": {
      width: 40,
      height: 40,
    },
  },
}))(Button);

const useImagesStyles = makeStyles((theme) => ({
  root: {
    margin: "0 10px 0 0",
    position: "relative",
  },
  previewImage: {
    maxWidth: 90,
    height: 90,
    objectFit: "cover",
    borderRadius: 7,
  },
  textBlock: {
    marginRight: 20,
  },
  removeButton: {
    position: "absolute",
    right: 1,
    bottom: 5,
    minWidth: "unset",
    color: theme.palette.error.main,
    padding: 2,
    borderRadius: "50%",
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "white",
  },
  svgIcon: {
    width: 40,
    height: "auto",
    padding: 3,
    opacity: 0.38,
  },
  qrInput: {
    width: 200,
    marginLeft: 20,
    marginTop: 20,
    marginBottom: 20,
  },
}));

/**
 * @param {String} src
 * @param {Function} removeButtonHandler
 * @param {Boolean} isAllowedToEdit
 * @param {Function} onImageClick - Обработчик клика по миниатюре (открывает попап просмотра).
 */
const Image = ({ src, removeButtonHandler, isAllowedToEdit, onImageClick }) => {
  const classes = useImagesStyles();

  return (
    <Box className={classes.root} data-tag="image wrapper">
      <img className={classes.previewImage} src={src} alt="" style={{ cursor: "zoom-in" }} onClick={onImageClick} />
      {isAllowedToEdit && (
        <>
          <div
            style={{ backgroundColor: "white", width: 12, height: 10, position: "absolute", right: 9, bottom: 14 }}
          />
          <Button className={classes.removeButton} onClick={removeButtonHandler}>
            <DeleteForeverIcon />
          </Button>
        </>
      )}
    </Box>
  );
};

Image.propTypes = {
  isAllowedToEdit: PropTypes.bool,
  onImageClick: PropTypes.func,
  removeButtonHandler: PropTypes.func.isRequired,
  src: PropTypes.string.isRequired,
};

/**
 * @param {ExerciseType7Answer|ExerciseType7Legacy} answer
 * @param {Boolean} isAllowedToEdit
 * @param {function} onCodeInput
 * @returns {*}
 * @constructor
 */
const Exercise7Images = ({ answer, isAllowedToEdit, onCodeInput }) => {
  const classes = useImagesStyles();
  const t = useTranslate();
  const pictureFileInput = useRef(/** @type {?HTMLInputElement} */ null);
  const {
    pictureFiles,
    setPictureFiles,
    homeworkUserDataPhotos,
    resetImage,
    removeImageFilesButtonHandler,
    removeHomeworkImageButtonHandler,
  } = useAnswerUserFiles();
  const {
    userInfo: { subject },
  } = useBaseData();
  const [isBackdropOpen, setBackdropOpen] = useState(false);
  const [isWebCamOpened, openWebcam] = useState(false);
  const [approveCode, setApproveCode] = useState("");
  // Индекс изображения в попапе просмотра хранится отдельно от признака открытия:
  // при закрытии индекс не сбрасывается, чтобы во время анимации закрытия
  // пользователь видел текущее изображение, а не первое из набора
  const [viewerIndex, setViewerIndex] = useState(0);
  const [isViewerOpened, setViewerOpened] = useState(false);

  // Список URL всех изображений (загруженные на сервер + локальные) для навигации в попапе.
  // useMemo, чтобы не создавать повторные blob-ссылки на каждый ререндер
  const viewerImages = useMemo(
    () => [
      ...homeworkUserDataPhotos.map((remoteMedia) => remoteMedia.url),
      ...pictureFiles.map((file) => URL.createObjectURL(file)),
    ],
    [homeworkUserDataPhotos, pictureFiles],
  );

  //reset state
  useEffect(() => {
    resetImage(answer);
    setBackdropOpen(false);
    openWebcam(false);
    setViewerOpened(false);
  }, [answer]);

  useEffect(() => {
    onCodeInput && onCodeInput(approveCode);
  }, [approveCode]);

  /**
   * Открывает попап просмотра изображения с заданным индексом.
   *
   * @param {Number} index - Индекс изображения в списке viewerImages.
   */
  const openViewer = (index) => {
    setViewerIndex(index);
    setViewerOpened(true);
  };

  /** @param {ReactEventTarget} target */
  const onPictureSelected = async ({ target }) => {
    const totalImageCount = pictureFiles.length + homeworkUserDataPhotos.length;
    if (totalImageCount === 5) {
      return;
    }
    /** @type {File[]} */
    const files = [...target.files].filter((file) => file.type.split("/")[0] === "image");
    const newCount = 5 - totalImageCount;
    const length = Math.min(files.length, newCount);
    setBackdropOpen(true);
    for (let i = 0; i < length; i++) {
      files[i] = await resizeImage(files[i]);
      pictureFiles.push(files[i]);
    }
    setPictureFiles([...pictureFiles]);
    setBackdropOpen(false);
    pictureFileInput.current.value = "";
  };

  /** */
  const galleryClickHandler = () => {
    pictureFileInput.current && pictureFileInput.current.click();
  };

  /** */
  const handleCamDialogClose = () => {
    openWebcam(false);
  };

  /** @param {String} image */
  const cameraDialogHandler = async (image) => {
    if (pictureFiles.length === 5) {
      return;
    }
    setPictureFiles([...pictureFiles, await resizeImage(new File([dataURLToBlob(image)], `photo-${Date.now()}.jpg`))]);
  };

  /** @param {ReactEventTarget} target */
  const ApproveCodeHandler = ({ target }) => {
    const value = target.value;
    setApproveCode(value);
  };

  const textBlock = subject.hmwksAddnlFlsEnabled ? t("Exercises.type7.addPhotoVideo") : t("Exercises.type7.addPhoto");

  return (
    <>
      <FlexboxVertical data-component="Images" alignSelf="stretch" margin="20px 20px 0">
        {isAllowedToEdit && (
          <FlexboxHorizontal data-tag="images file buttons" alignItems="center" justifyContent="center">
            <Typography className={classes.textBlock}>{textBlock}</Typography>
            <StyledButton onClick={() => openWebcam(true)}>
              <PhotoCameraRoundedIcon />
            </StyledButton>
            <StyledButton onClick={galleryClickHandler}>
              <WallpaperRoundedIcon />
            </StyledButton>
            {subject.hmwksAddnlFlsEnabled && <VideoCamButton />}
            {(answer.hasCodeToApprove || (answer.data && answer.data.hasCodeToApprove)) && (
              <TextField
                className={classes.qrInput}
                onChange={ApproveCodeHandler}
                value={approveCode}
                name="acceptance code"
                label={t("Exercises.type7.codeToApprove")}
                variant="outlined"
                fullWidth={false}
              />
            )}
          </FlexboxHorizontal>
        )}
        <FlexboxHorizontal
          data-tag="image previews"
          marginTop="10px"
          marginBottom={homeworkUserDataPhotos?.length > 0 || pictureFiles?.length > 0 ? "10px" : undefined}
        >
          {homeworkUserDataPhotos.map((remoteMedia, i) => (
            <Image
              key={remoteMedia.name + i}
              src={remoteMedia.url}
              removeButtonHandler={removeHomeworkImageButtonHandler(i)}
              isAllowedToEdit={isAllowedToEdit}
              onImageClick={() => openViewer(i)}
            />
          ))}
          {pictureFiles.map((file, i) => (
            <Image
              key={file.name + i}
              src={URL.createObjectURL(file)}
              removeButtonHandler={removeImageFilesButtonHandler(i)}
              isAllowedToEdit
              // Локальные файлы идут в списке после загруженных на сервер
              onImageClick={() => openViewer(homeworkUserDataPhotos.length + i)}
            />
          ))}
        </FlexboxHorizontal>
      </FlexboxVertical>
      <WebCamDialog open={isWebCamOpened} onClose={handleCamDialogClose} onCaptured={cameraDialogHandler} />
      <ImageViewerDialog
        open={isViewerOpened}
        images={viewerImages}
        activeIndex={viewerIndex}
        onNavigate={setViewerIndex}
        onClose={() => setViewerOpened(false)}
      />
      <MyBackdrop open={isBackdropOpen} />
      <input
        style={{ display: "none" }}
        type="file"
        ref={pictureFileInput}
        accept="image/png, image/jpeg"
        onChange={onPictureSelected}
        multiple
      />
    </>
  );
};

Exercise7Images.propTypes = {
  answer: PropTypes.object.isRequired,
  isAllowedToEdit: PropTypes.bool,
  onCodeInput: PropTypes.func,
};

export default Exercise7Images;
